import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import { useLocation, useParams } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

type TCrumb = {
  label: string;
  href?: string;
};

const ADMIN_LABELS: Record<string, string> = {
  analytics: "Analytics",
  "ai-usage": "AI Usage",
  "audit-logs": "Audit Logs",
  chat: "Chat",
  companies: "Companies",
  files: "Files",
  gdpr: "GDPR",
  "investor-invites": "Investor Invitations",
  sales: "Sales",
  "investor-onboarding-jobs": "Investor Onboarding Jobs",
  "investor-platform": "Investor Platform",
  legal: "Legal",
  "login-attempts": "Login Attempts",
  "onboarding-answers": "Onboarding Answers",
  "onboarding-questions": "Onboarding Questions",
  reviewers: "Reviewers",
  roles: "Roles",
  sections: "Sections",
  settings: "Settings",
  system: "System",
  users: "Users",
  verification: "Verification",
  "whatsapp-groups": "WhatsApp Groups",
  "whatsapp-templates": "WhatsApp Templates",
};

function useBreadcrumbs(): TCrumb[] {
  const location = useLocation();
  const params = useParams({ strict: false });
  const companies = useStore((state) => state.companies);
  const threadTitles = useChatStore((state) => state.threadTitles);

  const pathname = location.pathname;
  const company_id = (params as Record<string, string | undefined>).company_id;
  const section_id = (params as Record<string, string | undefined>).section_id;
  const thread_id = (params as Record<string, string | undefined>).thread_id;

  // Admin
  if (pathname.startsWith("/app/admin")) {
    const crumbs: TCrumb[] = [{ label: "Admin", href: "/app/admin/analytics" }];
    const segment = pathname.replace("/app/admin/", "").split("/")[0];
    if (segment && ADMIN_LABELS[segment]) {
      crumbs.push({ label: ADMIN_LABELS[segment] });
    }
    return crumbs;
  }

  // Top-level pages — plain text only, no links
  if (pathname.startsWith("/app/settings")) return [];
  if (pathname.startsWith("/app/reviews")) return [{ label: "Reviews" }];
  if (pathname.startsWith("/app/onboarding")) return [{ label: "Onboarding" }];

  if (!company_id) return [];

  const company = companies?.find((c) => c.id === company_id);
  const sections = Array.isArray(company?.sections) ? company.sections : [];
  const section = sections.find((s) => s.id === section_id);
  const firstSection = sections[0];

  const isDashboard = pathname.endsWith("/dashboard");
  const isNewChat = pathname.endsWith("/new");
  const isThread = pathname.includes("/threads/") && !!thread_id;
  const isInvestorChat = pathname.includes("/investor-chat/") && !!thread_id;
  const isDeepPage = isNewChat || isThread || isInvestorChat;

  const crumbs: TCrumb[] = [];

  // Company crumb:
  // - On dashboard: plain text (you're already "at" the company context)
  // - On deeper pages: links to /app (company list — a genuinely different destination)
  crumbs.push({
    label: company?.name ?? company_id,
    href: firstSection ? `/app/companies/${company_id}/sections/${firstSection.id}/dashboard` : undefined,
  });

  // Company-level onboarding
  if (pathname.includes("/onboarding")) {
    crumbs.push({ label: "Onboarding" });
    if (pathname.endsWith("/summary")) {
      crumbs[crumbs.length - 1].href = `/app/companies/${company_id}/onboarding`;
      crumbs.push({ label: "Summary" });
    }
    return crumbs;
  }

  // Section crumb:
  // - On dashboard: plain text (it IS the current page)
  // - On deeper pages: links to section dashboard (genuine back-navigation)
  if (section_id && section) {
    crumbs.push({
      label: section.name ?? section_id,
      href: isDeepPage ? `/app/companies/${company_id}/sections/${section_id}/dashboard` : undefined,
    });
  }

  // Current page crumb — always plain text
  if (isDashboard) {
    crumbs.push({ label: "Dashboard" });
  } else if (isNewChat) {
    crumbs.push({ label: "New Chat" });
  } else if (isThread) {
    crumbs.push({ label: (thread_id && threadTitles[thread_id]) ?? "Chat" });
  } else if (isInvestorChat) {
    crumbs.push({ label: (thread_id && threadTitles[thread_id]) ?? "Investor Chat" });
  }

  return crumbs;
}

export function AppBreadcrumb() {
  const crumbs = useBreadcrumbs();

  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="hidden min-w-0 items-center sm:flex"
    >
      <ol className="flex items-center gap-1">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li
              key={index}
              className="flex items-center gap-1"
            >
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
              {!isLast && crumb.href ? (
                <a
                  href={crumb.href}
                  className="max-w-[120px] truncate text-sm text-muted-foreground transition-colors hover:text-foreground lg:max-w-[180px]"
                >
                  {crumb.label}
                </a>
              ) : (
                <span className="max-w-[120px] truncate text-sm font-medium text-foreground lg:max-w-[180px]">
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
